import { useState, useEffect, useCallback, useRef } from "react";
import { getDocumentCountersService } from "../service/uploadFlow.service";

/**
 * Hook customizado para buscar contadores de documentos de um evento.
 *
 * Este hook utiliza o endpoint otimizado que retorna APENAS os contadores
 * sem carregar a lista completa de documentos. Ideal para badges, resumos
 * e dashboards onde você só precisa exibir quantidades.
 *
 * @param {number} eventId - ID do evento (obrigatório)
 * @param {Object} filters - Filtros opcionais para a busca
 * @param {number} [filters.companyId] - Filtrar por empresa específica
 * @param {string} [filters.uploadOrigin] - Filtrar por origem (ECOMMERCE, PARTNER, CLIENT, CONTRACT)
 * @param {number} [filters.approvalInstanceId] - Filtrar por instância de aprovação
 * @param {number} [filters.documentChecklistId] - Filtrar por checklist
 * @param {string} [filters.documentType] - Filtrar por tipo de documento
 * @param {boolean} [filters.dispensed] - Filtrar documentos dispensados
 * @param {string} [filters.documentStatus] - Filtrar por status
 * @param {Object} [filters.headers] - Headers customizados para autenticação
 * @param {Object} options - Opções do hook
 * @param {boolean} [options.enabled=true] - Se false, não faz a chamada automaticamente
 * @param {number} [options.pollingInterval] - Intervalo em ms para polling automático
 *
 * @returns {Object} Objeto com:
 *   - counters: Objeto com os contadores (pendingCount, approvedCount, rejectedCount, waitingSendCount, dispensedCount)
 *   - loading: Boolean indicando se está carregando
 *   - error: Objeto de erro caso ocorra
 *   - refetch: Função para recarregar os contadores manualmente
 *
 * @example
 * // Uso básico
 * const { counters, loading, error } = useDocumentCounters(eventId);
 *
 * @example
 * // Com filtros
 * const { counters } = useDocumentCounters(eventId, {
 *   companyId: 987,
 *   uploadOrigin: 'CLIENT'
 * });
 *
 * @example
 * // Com polling a cada 30 segundos
 * const { counters, refetch } = useDocumentCounters(
 *   eventId,
 *   {},
 *   { pollingInterval: 30000 }
 * );
 *
 * @example
 * // Desabilitado até que alguma condição seja atendida
 * const { counters, refetch } = useDocumentCounters(
 *   eventId,
 *   {},
 *   { enabled: isAuthenticated }
 * );
 */
const useDocumentCounters = (eventId, filters = {}, options = {}) => {
  const { enabled = true, pollingInterval } = options;

  const [counters, setCounters] = useState({
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    waitingSendCount: 0,
    dispensedCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Usa ref para evitar loops infinitos com dependências
  const filtersRef = useRef(filters);
  const pollingIntervalRef = useRef();

  // Atualiza a ref quando os filtros mudam
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchCounters = useCallback(async () => {
    if (!eventId) {
      setError(new Error("eventId é obrigatório"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getDocumentCountersService({
        eventId,
        ...filtersRef.current,
      });

      // Axios retorna os dados em response.data
      const data = response.data;

      // Mantém os mesmos nomes do backend para consistência (Clean Code)
      setCounters({
        pendingCount: data.pendingCount || 0,
        approvedCount: data.approvedCount || 0,
        rejectedCount: data.rejectedCount || 0,
        waitingSendCount: data.waitingSendCount || 0,
        dispensedCount: data.dispensedCount || 0,
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "document_counters_error_fetch";

      setError({
        message: errorMessage,
        status: err.response?.status,
        originalError: err,
      });

      console.error("Erro ao buscar contadores de documentos:", err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Efeito principal para buscar os contadores
  useEffect(() => {
    if (!enabled || !eventId) {
      return;
    }

    fetchCounters();
  }, [enabled, eventId, fetchCounters]);

  // Efeito para polling (se configurado)
  useEffect(() => {
    if (!enabled || !pollingInterval || !eventId) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchCounters();
    }, pollingInterval);

    pollingIntervalRef.current = intervalId;

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [enabled, pollingInterval, eventId, fetchCounters]);

  // Função para forçar uma recarga manual
  const refetch = useCallback(() => {
    return fetchCounters();
  }, [fetchCounters]);

  return {
    counters,
    loading,
    error,
    refetch,
  };
};

export default useDocumentCounters;
